import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { rem } from "polished";
import React from "react";

import { Flex } from "../../base";
import IconCheck from "../IconCheck";
import { LoadingRingWhite } from "../LoadingRing";

interface StepElementProps {
  active?: boolean;
}

const StepContainer = styled.div<StepElementProps>`
  position: relative;
  width: ${rem(30)};

  display: flex;
  flex-direction: column;
  align-items: center;

  opacity: ${({ active }) => (active ? "1" : "0.3")};
`;

const StepIcon = styled.div<StepElementProps>`
  width: ${rem(30)};
  height: ${rem(30)};
  margin-bottom: ${rem(15)};

  border-radius: ${rem(30)};
  background-color: ${({ active, theme }) =>
    active ? theme.color.start : "rgba(0, 0, 0, 0.6)"};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const StepNumber = styled.span`
  font-size: ${rem(16)};
  font-weight: 500;
  color: #fff;
`;

const StepText = styled.h3`
  width: auto;

  font-size: ${rem(16)};
  font-weight: 500;
  opacity: 0.6;
  color: #000;

  white-space: nowrap;
`;

const HorizontalLine = styled.div`
  width: ${rem(140)};
  height: 1px;
  background-color: #d8d8d8;

  margin: ${rem(14)} ${rem(14)} 0;
`;

interface StepProps {
  number: number;
  text: string;
  loading: boolean;
  active: boolean;
  complete: boolean;
}

const Step = ({
  number = 1,
  text = "",
  active = false,
  complete = false,
  loading = false,
}: StepProps) => {
  const Icon = complete ? (
    <IconCheck
      css={css`
        width: ${rem(16)};
        height: ${rem(12)};
      `}
      fill="#fff"
    />
  ) : loading ? (
    <LoadingRingWhite />
  ) : (
    <StepNumber>{number}</StepNumber>
  );
  return (
    <StepContainer active={active}>
      <StepIcon active={active || complete}>{Icon}</StepIcon>
      <StepText>{text}</StepText>
    </StepContainer>
  );
};

const StepsContainer = styled(Flex)`
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;

  margin-left: ${rem(80)};
  margin-right: ${rem(80)};
`;

interface StepsProps {
  steps: Array<{ number: number; text: string }>;
  current: number;
  loading?: boolean;
}

const StepsList = ({ steps, current, loading }: StepsProps) => (
  <StepsContainer>
    {steps.map((step, i) => (
      <React.Fragment key={step.number}>
        <Step
          key={step.number}
          {...step}
          active={current === step.number}
          complete={current > step.number}
          loading={step.number === current && loading}
        />
        {i < steps.length - 1 && <HorizontalLine key={`${step.number}-line`} />}
      </React.Fragment>
    ))}
  </StepsContainer>
);

export default StepsList;
